import React, { useMemo } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Tv, Repeat, Maximize2, AlertCircle, CheckCircle2, Trash2, Flag } from 'lucide-react';

const PANNEAU_TYPES = [
  { key: 'LED Digital',  label: 'LED Digital',  icon: Tv,        accent: 'bg-blue-50 text-blue-600 border-blue-200',          ringActive: 'ring-2 ring-blue-400 border-blue-300',          defaultPrix: 5000 },
  { key: 'Déroulant',    label: 'Déroulant',    icon: Repeat,    accent: 'bg-emerald-50 text-emerald-600 border-emerald-200', ringActive: 'ring-2 ring-emerald-400 border-emerald-300',    defaultPrix: 3000 },
  { key: 'Grand Format', label: 'Grand Format', icon: Maximize2, accent: 'bg-amber-50 text-amber-600 border-amber-200',       ringActive: 'ring-2 ring-amber-400 border-amber-300',        defaultPrix: 4000 },
  { key: 'Poteau',       label: 'Poteau',       icon: Flag,      accent: 'bg-orange-50 text-orange-600 border-orange-200',    ringActive: 'ring-2 ring-orange-400 border-orange-300',      defaultPrix: 2500 },
];

const fmtFCFA = (n) => {
  const v = Math.round(Number(n) || 0);
  return v.toLocaleString('fr-FR').replace(/,/g, ' ') + ' FCFA';
};

export const computeLineTotal = (l) => {
  const p = Number(l.prix_unitaire_jour) || 0;
  const d = Number(l.duree_jours) || 0;
  // Priorité au nouveau modèle : somme des affiches par emplacement × prix × durée
  if (l.affiches_par_emplacement && typeof l.affiches_par_emplacement === 'object') {
    const emps = Array.isArray(l.emplacements) ? l.emplacements : [];
    const totalAffiches = emps.reduce((s, e) => s + (Number(l.affiches_par_emplacement[e]) || 0), 0);
    if (totalAffiches > 0) return totalAffiches * p * d;
  }
  const n = Number(l.nombre_affiches) || 0;
  return n * p * d;
};

const computeDateFin = (date_debut, duree_jours) => {
  if (!date_debut || !duree_jours) return '';
  try {
    const d = new Date(date_debut);
    if (Number.isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + (Number(duree_jours) || 0));
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

const emptyLine = (typeKey, qty = 1) => ({
  type_panneau: typeKey,
  nombre_affiches: qty,
  emplacement: '',           // string concaténée pour rétrocompat
  emplacements: [],          // tableau des emplacements sélectionnés
  affiches_par_emplacement: {}, // map { nom_emplacement: nb_affiches (1..10) }
  prix_unitaire_jour: PANNEAU_TYPES.find(t => t.key === typeKey)?.defaultPrix || 0,
  duree_jours: 30,
  date_debut: '',
  date_fin: '',
  surface: '',
  dimension: '',
  frais_conception: qty * 20000,
  frais_conception_auto: true,
});

export default function DevisLignesMulti({ lignes, setLignes, mode = 'multi', setMode, showContractFields = false, emplacementsOptions = [], emplacementsByType = null, allowFull = false }) {
  const totalGeneral = useMemo(
    () => lignes.reduce((s, l) => s + computeLineTotal(l), 0),
    [lignes]
  );

  const getLineByType = (typeKey) => lignes.find(l => l.type_panneau === typeKey);
  const getQty = (typeKey) => Number(getLineByType(typeKey)?.nombre_affiches) || 0;

  // Met à jour la quantité d'un type. Si qty=0 → retire la ligne. Si type absent → l'ajoute.
  const setTypeQty = (typeKey, qty) => {
    const q = Math.max(0, Number(qty) || 0);
    setLignes((prev) => {
      const exists = prev.some(l => l.type_panneau === typeKey);
      if (q === 0) {
        return prev.filter(l => l.type_panneau !== typeKey);
      }
      if (!exists) {
        if (mode === 'single') return [emptyLine(typeKey, q)];
        return [...prev, emptyLine(typeKey, q)];
      }
      return prev.map(l => {
        if (l.type_panneau !== typeKey) return l;
        const next = { ...l, nombre_affiches: q };
        // Si la quantité diminue, tronquer la sélection d'emplacements
        if (Array.isArray(next.emplacements) && next.emplacements.length > q) {
          next.emplacements = next.emplacements.slice(0, q);
          next.emplacement = next.emplacements.join(' + ');
        }
        // Nettoyer les entrées orphelines du map affiches_par_emplacement
        if (next.affiches_par_emplacement && Array.isArray(next.emplacements)) {
          const cleaned = {};
          next.emplacements.forEach(e => {
            if (next.affiches_par_emplacement[e] != null) cleaned[e] = next.affiches_par_emplacement[e];
          });
          next.affiches_par_emplacement = cleaned;
        }
        if (next.frais_conception_auto !== false) {
          next.frais_conception = q * 20000;
        }
        return next;
      });
    });
  };

  const updateLineByType = (typeKey, field, value) => {
    setLignes((prev) => prev.map((l) => {
      if (l.type_panneau !== typeKey) return l;
      const next = { ...l, [field]: value };
      if (field === 'duree_jours' || field === 'date_debut') {
        const newFin = computeDateFin(
          field === 'date_debut' ? value : next.date_debut,
          field === 'duree_jours' ? value : next.duree_jours,
        );
        if (newFin) next.date_fin = newFin;
      }
      if (field === 'frais_conception') {
        next.frais_conception_auto = false;
      }
      return next;
    }));
  };

  // Toggle l'emplacement dans la liste sélectionnée pour un type donné
  const toggleEmplacement = (typeKey, empValue) => {
    setLignes((prev) => prev.map((l) => {
      if (l.type_panneau !== typeKey) return l;
      const current = Array.isArray(l.emplacements) ? l.emplacements : (l.emplacement ? l.emplacement.split(' + ') : []);
      const max = Number(l.nombre_affiches) || 0;
      const currentMap = { ...(l.affiches_par_emplacement || {}) };
      let next;
      if (current.includes(empValue)) {
        next = current.filter(e => e !== empValue);
        delete currentMap[empValue];
      } else {
        if (current.length >= max) return l; // limite atteinte
        next = [...current, empValue];
        // Initialise à 1 affiche par défaut
        currentMap[empValue] = 1;
      }
      return { ...l, emplacements: next, emplacement: next.join(' + '), affiches_par_emplacement: currentMap };
    }));
  };

  // Capacité d'affiches par emplacement selon le type — 25 pour Poteau, 10 sinon
  const maxAffichesForType = (typeKey) => (typeKey === 'Poteau' ? 25 : 10);

  // Met à jour le nombre d'affiches pour un emplacement précis (1..cap)
  const setAffichesForEmp = (typeKey, empValue, val) => {
    const raw = Number(val) || 0;
    const cap = maxAffichesForType(typeKey);
    const clamped = Math.max(1, Math.min(cap, raw));
    setLignes((prev) => prev.map((l) => {
      if (l.type_panneau !== typeKey) return l;
      const map = { ...(l.affiches_par_emplacement || {}) };
      map[empValue] = clamped;
      return { ...l, affiches_par_emplacement: map };
    }));
  };

  return (
    <div className="space-y-3" data-testid="devis-lignes-multi">
      {/* Sélecteur de mode */}
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-sm font-semibold">Types de panneaux *</Label>
        <button
          type="button"
          onClick={() => { setMode('single'); setLignes((p) => p.slice(0, 1)); }}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${mode === 'single' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
          data-testid="mode-single-btn"
        >
          Un seul type
        </button>
        <button
          type="button"
          onClick={() => setMode('multi')}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${mode === 'multi' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
          data-testid="mode-multi-btn"
        >
          Multiples
        </button>
      </div>

      {/* ÉTAPE 1 : Quantité par type de panneau */}
      <Card className="border border-slate-200 bg-slate-50/50">
        <CardContent className="p-3 sm:p-4">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">
            Étape 1 — Choisir la quantité par type de panneau
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PANNEAU_TYPES.map((t) => {
              const Icon = t.icon;
              const qty = getQty(t.key);
              const active = qty > 0;
              return (
                <div
                  key={t.key}
                  className={`p-3 rounded-lg border-2 transition min-w-0 overflow-hidden ${active ? `${t.ringActive} bg-white` : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  data-testid={`type-card-${t.key.replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${t.accent}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className="text-sm font-semibold text-slate-800 flex-1">{t.label}</span>
                    <span className="text-[11px] text-slate-500">{fmtFCFA(t.defaultPrix)}/j</span>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <Label htmlFor={`qty-${t.key}`} className="text-xs text-slate-500 whitespace-nowrap">
                      Nb panneaux :
                    </Label>
                    <div className="flex items-center rounded-lg border border-slate-300 bg-white overflow-hidden flex-shrink-0">
                      <button type="button" onClick={() => setTypeQty(t.key, Math.max(0, qty - 1))}
                        className="w-8 h-9 text-lg font-bold text-slate-600 hover:bg-slate-100 transition-colors" data-testid={`type-qty-minus-${t.key.replace(/\s+/g, '-')}`}>−</button>
                      <input
                        id={`qty-${t.key}`}
                        type="text"
                        inputMode="numeric"
                        value={qty}
                        onChange={(e) => setTypeQty(t.key, e.target.value.replace(/\D/g, '') || 0)}
                        className="w-10 h-9 text-center text-base font-extrabold text-slate-900 border-x border-slate-200 focus:outline-none"
                        data-testid={`type-qty-${t.key.replace(/\s+/g, '-')}`}
                      />
                      <button type="button" onClick={() => setTypeQty(t.key, qty + 1)}
                        className="w-8 h-9 text-lg font-bold text-blue-600 hover:bg-blue-50 transition-colors" data-testid={`type-qty-plus-${t.key.replace(/\s+/g, '-')}`}>+</button>
                    </div>
                    {active && (
                      <button
                        type="button"
                        onClick={() => setTypeQty(t.key, 0)}
                        className="ml-auto text-red-500 hover:text-red-700 p-1"
                        title="Retirer ce type"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ÉTAPE 2 : Détails par type sélectionné (quantité > 0) */}
      {lignes.length === 0 ? (
        <div className="p-4 rounded-lg border border-dashed border-slate-300 text-center text-sm text-slate-500" data-testid="lignes-empty">
          Indiquez au moins 1 panneau dans l'étape 1 pour continuer.
        </div>
      ) : (
        <div className="space-y-3" data-testid="lignes-list">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Étape 2 — Sélection des emplacements et détails
          </p>
          {lignes.map((l) => {
            const meta = PANNEAU_TYPES.find(t => t.key === l.type_panneau) || PANNEAU_TYPES[0];
            const Icon = meta.icon;
            const lineTotal = computeLineTotal(l);
            const qty = Number(l.nombre_affiches) || 0;
            const selected = Array.isArray(l.emplacements) ? l.emplacements : (l.emplacement ? l.emplacement.split(' + ').filter(Boolean) : []);
            const typedOptions = emplacementsByType?.[l.type_panneau] || [];
            const options = typedOptions.length > 0 ? typedOptions : (emplacementsOptions || []);
            const selectionComplete = selected.length === qty;
            const tooFew = selected.length < qty;
            return (
              <Card key={l.type_panneau} className="border border-slate-200" data-testid={`ligne-card-${l.type_panneau.replace(/\s+/g, '-')}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.accent}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <h4 className="text-sm font-bold text-[#1C2A46] flex-1">
                      {l.type_panneau} <span className="text-slate-400 font-normal">· {qty} panneau{qty > 1 ? 'x' : ''}</span>
                    </h4>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                    <div>
                      <Label className="text-xs">Prix unit. (FCFA / jour) *</Label>
                      <Input type="number" min={0} value={l.prix_unitaire_jour}
                        onChange={(e) => updateLineByType(l.type_panneau, 'prix_unitaire_jour', e.target.value)}
                        className="h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs">Durée (jours) *</Label>
                      <Input type="number" min={1} value={l.duree_jours}
                        onChange={(e) => updateLineByType(l.type_panneau, 'duree_jours', e.target.value)}
                        className="h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs">Date début *</Label>
                      <Input type="date" value={l.date_debut}
                        onChange={(e) => updateLineByType(l.type_panneau, 'date_debut', e.target.value)}
                        className="h-9 text-sm"
                        data-testid={`ligne-date-debut-${l.type_panneau.replace(/\s+/g, '-')}`} />
                    </div>
                  </div>

                  {/* Sélection multiple d'emplacements */}
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                        Emplacements à réserver
                      </Label>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold ${
                          selectionComplete
                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                            : tooFew
                            ? 'bg-amber-100 text-amber-700 border border-amber-300'
                            : 'bg-red-100 text-red-700 border border-red-300'
                        }`}
                        data-testid={`emp-count-${l.type_panneau.replace(/\s+/g, '-')}`}
                      >
                        {selectionComplete ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                        {selected.length} / {qty} sélectionné{selected.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    {options.length === 0 ? (
                      <Input
                        value={l.emplacement}
                        onChange={(e) => {
                          const v = e.target.value;
                          setLignes((prev) => prev.map(li =>
                            li.type_panneau === l.type_panneau ? { ...li, emplacement: v, emplacements: v ? v.split(/[,;]| \+ /).map(s => s.trim()).filter(Boolean) : [] } : li
                          ));
                        }}
                        placeholder="Ex: Avenue Boganda, Place de la République…"
                        className="h-9 text-sm"
                        data-testid={`emplacement-input-${l.type_panneau.replace(/\s+/g, '-')}`}
                      />
                    ) : (
                      <div className="max-h-[400px] overflow-y-auto space-y-1 pr-1" data-testid={`emp-list-${l.type_panneau.replace(/\s+/g, '-')}`}>
                        {options.map((opt, idx) => {
                          const value = opt.value ?? opt;
                          const label = opt.label ?? opt;
                          const isChecked = selected.includes(value);
                          const isFull = !allowFull && opt.full === true;
                          const disabled = isFull || (!isChecked && selected.length >= qty);
                          const available = Number(opt.available ?? 10);
                          const capacity = Number(opt.capacity ?? 10);
                          const occupants = Array.isArray(opt.occupants) ? opt.occupants : [];
                          return (
                            <label
                              key={`${value}-${idx}`}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm transition ${
                                isFull
                                  ? 'opacity-60 cursor-not-allowed bg-red-50 border border-red-200'
                                  : isChecked
                                  ? 'bg-blue-50 border border-blue-200'
                                  : disabled
                                  ? 'opacity-50 cursor-not-allowed bg-white border border-slate-100'
                                  : 'bg-white border border-slate-100 hover:bg-slate-50'
                              }`}
                              title={
                                occupants.length > 0
                                  ? occupants.map(o => `${o.entreprise} — ${o.affiches} affiche(s), jusqu'au ${o.date_fin || '?'}`).join('\n')
                                  : ''
                              }
                            >
                              <Checkbox
                                checked={isChecked}
                                disabled={disabled}
                                onCheckedChange={() => !disabled && toggleEmplacement(l.type_panneau, value)}
                                className="h-4 w-4"
                                data-testid={`emp-check-${l.type_panneau.replace(/\s+/g, '-')}-${idx}`}
                              />
                              <span className="flex-1 text-slate-800 truncate">{label}</span>
                              <span
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${
                                  isFull
                                    ? 'bg-red-100 text-red-700 border border-red-300'
                                    : available < capacity
                                    ? 'bg-amber-100 text-amber-700 border border-amber-300'
                                    : 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                }`}
                                data-testid={`emp-availability-${idx}`}
                              >
                                {isFull ? '🔒 Complet' : `${available}/${capacity} dispo`}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* ÉTAPE 3 : Nombre d'affiches par emplacement (max selon type) */}
                  {selected.length > 0 && (
                    <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3" data-testid={`affiches-block-${l.type_panneau.replace(/\s+/g, '-')}`}>
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                          {l.type_panneau === 'Poteau' ? "Nombre de poteaux par avenue" : "Nombre d'affiches par emplacement"} <span className="text-[10px] text-slate-500 normal-case">· max {maxAffichesForType(l.type_panneau)} par {l.type_panneau === 'Poteau' ? 'avenue' : 'panneau'}</span>
                        </Label>
                        {(() => {
                          const total = selected.reduce((s, e) => s + (Number(l.affiches_par_emplacement?.[e]) || 0), 0);
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-indigo-100 text-indigo-800 border border-indigo-300">
                              Total : {total} affiche{total > 1 ? 's' : ''}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {selected.map((empName) => {
                          const val = Number(l.affiches_par_emplacement?.[empName]) || 1;
                          return (
                            <div key={empName} className="flex items-center gap-2 bg-white border border-indigo-100 rounded p-2">
                              <span className="text-sm text-slate-800 flex-1 truncate" title={empName}>{empName}</span>
                              <Input
                                type="number"
                                min={1}
                                max={maxAffichesForType(l.type_panneau)}
                                step={1}
                                value={val}
                                onChange={(e) => setAffichesForEmp(l.type_panneau, empName, e.target.value)}
                                className="h-8 w-16 text-center text-sm font-bold"
                                data-testid={`affiches-input-${l.type_panneau.replace(/\s+/g, '-')}-${empName.replace(/\s+/g, '-')}`}
                              />
                              <span className="text-[10px] text-slate-500 whitespace-nowrap">/ {maxAffichesForType(l.type_panneau)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                    <div>
                      <Label className="text-xs">Date fin (auto)</Label>
                      <Input type="date" value={l.date_fin || ''} readOnly className="h-9 text-sm bg-slate-50" />
                    </div>
                    {showContractFields && (
                      <>
                        <div>
                          <Label className="text-xs">Dimension</Label>
                          <Input value={l.dimension || ''}
                            onChange={(e) => updateLineByType(l.type_panneau, 'dimension', e.target.value)}
                            placeholder="Ex: 2m8/1m4" className="h-9 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs">
                            Frais conception (20 000 × nb){' '}
                            {l.frais_conception_auto !== false && <span className="text-emerald-600 ml-1">· auto</span>}
                          </Label>
                          <Input type="number" min={0} value={l.frais_conception ?? 0}
                            onChange={(e) => updateLineByType(l.type_panneau, 'frais_conception', e.target.value)}
                            className="h-9 text-sm" />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <p className="text-xs text-slate-500">
                      {(() => {
                        const selArr = Array.isArray(l.emplacements) ? l.emplacements : [];
                        const totAff = selArr.reduce((s, e) => s + (Number(l.affiches_par_emplacement?.[e]) || 0), 0);
                        const eff = totAff > 0 ? totAff : qty;
                        return `${eff} aff. × ${fmtFCFA(l.prix_unitaire_jour)}/j × ${(Number(l.duree_jours) || 0)} j`;
                      })()}
                    </p>
                    <p className="text-base font-bold text-blue-700" data-testid={`ligne-total-${l.type_panneau.replace(/\s+/g, '-')}`}>
                      {fmtFCFA(lineTotal)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Total général */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-4 flex items-center justify-between" data-testid="devis-total-general">
            <div>
              <p className="text-xs opacity-80">TOTAL TTC ({lignes.length} type{lignes.length > 1 ? 's' : ''}, {lignes.reduce((s, l) => s + (Number(l.nombre_affiches) || 0), 0)} panneau{lignes.reduce((s, l) => s + (Number(l.nombre_affiches) || 0), 0) > 1 ? 'x' : ''})</p>
              <p className="text-2xl font-bold">{fmtFCFA(totalGeneral)}</p>
            </div>
            <div className="text-right text-xs opacity-90">
              TVA non applicable<br />
              Paiement : 50% à la commande
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
